import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { DeleteConfirmDialog } from "../components/pipeline/DeleteConfirmDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Building, 
  Building2,
  Plus, 
  Edit2, 
  Trash2, 
  Phone, 
  Mail, 
  User, 
  Calendar,
  FolderOpen,
  CheckCircle2,
  XCircle,
  Save,
  X
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProvidersPage = () => {
  const { user, canDelete } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, title: "", message: "", onConfirm: null });
  const [selectedProvider, setSelectedProvider] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: "",
    nombre_fiscal: "",
    cif: "",
    contacto_nombre: "",
    contacto_telefono: "",
    contacto_email: "",
    direccion: "",
    fecha_inicio_colaboracion: "",
    notas: "",
    is_active: true,
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/providers`);
      setProviders(response.data);
    } catch (error) {
      console.error("Error fetching providers:", error);
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: "",
      nombre_fiscal: "",
      cif: "",
      contacto_nombre: "",
      contacto_telefono: "",
      contacto_email: "",
      direccion: "",
      fecha_inicio_colaboracion: "",
      notas: "",
      is_active: true,
    });
  };

  const handleCreate = async () => {
    if (!formData.nombre) {
      toast.error("El nombre del proveedor es obligatorio");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/providers`, formData);
      toast.success("Proveedor creado correctamente");
      setCreateModalOpen(false);
      resetForm();
      fetchProviders();
    } catch (error) {
      console.error("Error creating provider:", error);
      toast.error("Error al crear proveedor");
    }
  };

  const handleUpdate = async () => {
    if (!selectedProvider || !formData.nombre) {
      toast.error("El nombre del proveedor es obligatorio");
      return;
    }

    try {
      await axios.put(`${API_URL}/api/providers/${selectedProvider.id}`, formData);
      toast.success("Proveedor actualizado correctamente");
      setEditModalOpen(false);
      setSelectedProvider(null);
      resetForm();
      fetchProviders();
    } catch (error) {
      console.error("Error updating provider:", error);
      toast.error("Error al actualizar proveedor");
    }
  };

  const handleDelete = async (providerId) => {
    setDeleteConfirm({
      open: true,
      title: "Eliminar proveedor",
      message: "¿Estás seguro de eliminar este proveedor? Esta acción no se puede deshacer.",
      onConfirm: async () => {
        setDeleteConfirm(prev => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API_URL}/api/providers/${providerId}`);
          toast.success("Proveedor eliminado");
          fetchProviders();
        } catch (error) {
          console.error("Error deleting provider:", error);
          toast.error(error.response?.data?.detail || "Error al eliminar proveedor");
        }
      },
    });
  };

  const handleToggleActive = async (provider) => {
    try {
      await axios.put(`${API_URL}/api/providers/${provider.id}`, { 
        is_active: !provider.is_active 
      });
      toast.success(`Proveedor ${!provider.is_active ? "activado" : "desactivado"}`);
      fetchProviders();
    } catch (error) {
      console.error("Error updating provider:", error);
      toast.error("Error al actualizar proveedor");
    }
  };

  const openEditModal = (provider) => {
    setSelectedProvider(provider);
    setFormData({
      nombre: provider.nombre || "",
      nombre_fiscal: provider.nombre_fiscal || "",
      cif: provider.cif || "",
      contacto_nombre: provider.contacto_nombre || "",
      contacto_telefono: provider.contacto_telefono || "",
      contacto_email: provider.contacto_email || "",
      direccion: provider.direccion || "",
      fecha_inicio_colaboracion: provider.fecha_inicio_colaboracion || "",
      notas: provider.notas || "",
      is_active: provider.is_active !== false,
    });
    setEditModalOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  const renderProviderForm = (onSubmit, submitText) => (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-slate-300">Nombre del Proveedor *</Label>
          <Input
            value={formData.nombre}
            onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
            placeholder="Ej: Telefónica, Vodafone..."
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            data-testid="provider-name-input"
          />
        </div>
        <div>
          <Label className="text-slate-300">Nombre Fiscal</Label>
          <Input
            value={formData.nombre_fiscal}
            onChange={(e) => setFormData({ ...formData, nombre_fiscal: e.target.value })}
            placeholder="Razón social completa"
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            data-testid="provider-nombre-fiscal-input"
          />
        </div>
        <div>
          <Label className="text-slate-300">CIF</Label>
          <Input
            value={formData.cif}
            onChange={(e) => setFormData({ ...formData, cif: e.target.value })}
            placeholder="A12345678"
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            data-testid="provider-cif-input"
          />
        </div>
        <div>
          <Label className="text-slate-300">Persona de Contacto</Label>
          <Input
            value={formData.contacto_nombre}
            onChange={(e) => setFormData({ ...formData, contacto_nombre: e.target.value })}
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            data-testid="provider-contact-name-input"
          />
        </div>
        <div>
          <Label className="text-slate-300">Teléfono</Label>
          <Input
            value={formData.contacto_telefono}
            onChange={(e) => setFormData({ ...formData, contacto_telefono: e.target.value })}
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            data-testid="provider-phone-input"
          />
        </div>
        <div>
          <Label className="text-slate-300">Email</Label>
          <Input
            type="email"
            value={formData.contacto_email}
            onChange={(e) => setFormData({ ...formData, contacto_email: e.target.value })}
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            data-testid="provider-email-input"
          />
        </div>
        <div>
          <Label className="text-slate-300">Fecha Inicio Colaboración</Label>
          <Input
            type="date"
            value={formData.fecha_inicio_colaboracion}
            onChange={(e) => setFormData({ ...formData, fecha_inicio_colaboracion: e.target.value })}
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-slate-300">Dirección</Label>
          <Input
            value={formData.direccion}
            onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            data-testid="provider-address-input"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-slate-300">Notas</Label>
          <Textarea
            value={formData.notas}
            onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
            rows={3}
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            data-testid="provider-notes-input"
          />
        </div>
        <div className="col-span-2 flex items-center gap-3">
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            data-testid="provider-active-switch"
          />
          <Label className="text-slate-300">Proveedor activo</Label>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-[#1E1E5F]">
        <Button
          variant="outline"
          onClick={() => {
            setCreateModalOpen(false);
            setEditModalOpen(false);
            resetForm();
          }}
          className="border-[#1E1E5F] text-slate-300"
        >
          <X className="h-4 w-4 mr-2" />
          Cancelar
        </Button>
        <Button onClick={onSubmit} className="bg-[#42E695] text-[#04042C]" data-testid="provider-submit-btn">
          <Save className="h-4 w-4 mr-2" />
          {submitText}
        </Button>
      </div>
    </div>
  );

  return (
    <>
    <div className="space-y-6" data-testid="providers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <Building2 className="h-8 w-8 text-[#03BEE4]" />
            Proveedores
          </h1>
          <p className="text-slate-400 mt-1">
            Gestiona los proveedores de servicios
          </p>
        </div>
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]"
              data-testid="new-provider-btn"
              onClick={() => {
                resetForm();
                setCreateModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Proveedor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center gap-2">
                <Building className="h-5 w-5 text-[#03BEE4]" />
                Nuevo Proveedor
              </DialogTitle>
            </DialogHeader>
            {renderProviderForm(handleCreate, "Crear Proveedor")}
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#03BEE4]/20 flex items-center justify-center">
                <Building className="h-6 w-6 text-[#03BEE4]" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Proveedores</p>
                <p className="text-2xl font-bold text-white">{providers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#42E695]/20 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-[#42E695]" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Activos</p>
                <p className="text-2xl font-bold text-[#42E695]">
                  {providers.filter(p => p.is_active !== false).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Inactivos</p>
                <p className="text-2xl font-bold text-red-400">
                  {providers.filter(p => p.is_active === false).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Providers Table */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <Building className="h-5 w-5 text-[#03BEE4]" />
            Lista de Proveedores
          </CardTitle>
          <CardDescription className="text-slate-400">
            {providers.length} proveedores registrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {providers.length === 0 ? (
            <div className="text-center py-12">
              <Building className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">No hay proveedores registrados</p>
              <Button 
                className="mt-4 bg-[#42E695] text-[#04042C]"
                onClick={() => setCreateModalOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Primer Proveedor
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1E1E5F]">
                    <TableHead className="text-slate-400">Proveedor</TableHead>
                    <TableHead className="text-slate-400">Contacto</TableHead>
                    <TableHead className="text-slate-400">Teléfono</TableHead>
                    <TableHead className="text-slate-400">Email</TableHead>
                    <TableHead className="text-slate-400">Inicio Colaboración</TableHead>
                    <TableHead className="text-slate-400">Estado</TableHead>
                    <TableHead className="text-slate-400">Carpeta SharePoint</TableHead>
                    <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providers.map((provider) => (
                    <TableRow key={provider.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#03BEE4]/20 flex items-center justify-center">
                            <Building className="h-5 w-5 text-[#03BEE4]" />
                          </div>
                          <span className="text-white font-medium">{provider.nombre}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-300">
                          <User className="h-4 w-4 text-slate-400" />
                          {provider.contacto_nombre || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-300">
                          <Phone className="h-4 w-4 text-slate-400" />
                          {provider.contacto_telefono || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-300">
                          <Mail className="h-4 w-4 text-slate-400" />
                          {provider.contacto_email || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-300">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {formatDate(provider.fecha_inicio_colaboracion)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {provider.is_active !== false ? (
                          <Badge className="bg-[#42E695]/20 text-[#42E695] border-[#42E695]/30">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Activo
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactivo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-[#03BEE4]">
                          <FolderOpen className="h-4 w-4" />
                          <span className="text-sm truncate max-w-[150px]">
                            {provider.sharepoint_folder || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditModal(provider)}
                            className="text-[#03BEE4] hover:text-[#03BEE4]/80"
                            data-testid={`edit-provider-${provider.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Switch
                            checked={provider.is_active !== false}
                            onCheckedChange={() => handleToggleActive(provider)}
                          />
                          {canDelete("providers") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(provider.id)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`delete-provider-${provider.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#03BEE4]" />
              Editar Proveedor
            </DialogTitle>
          </DialogHeader>
          {renderProviderForm(handleUpdate, "Guardar Cambios")}
        </DialogContent>
      </Dialog>
    </div>

    <DeleteConfirmDialog
      open={deleteConfirm.open}
      onOpenChange={(open) => !open && setDeleteConfirm(prev => ({ ...prev, open: false }))}
      onConfirm={deleteConfirm.onConfirm}
      onCancel={() => setDeleteConfirm(prev => ({ ...prev, open: false }))}
      title={deleteConfirm.title}
      message={deleteConfirm.message}
    />
    </>
  );
};

export default ProvidersPage;
